<template>
  <div class="comments">
    <div class="comments__header">
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="comments__icon">
        <path d="M14 2H2a1 1 0 00-1 1v8a1 1 0 001 1h3l3 2 3-2h3a1 1 0 001-1V3a1 1 0 00-1-1z"/>
      </svg>
      <span class="comments__title">Comments</span>
      <span class="comments__count" v-if="comments.length">{{ comments.length }}</span>
    </div>

    <div v-if="loading" class="comments__loading">
      <Loading size="16" :dark="true" />
    </div>

    <template v-else>
      <div class="comments__list" v-if="comments.length" ref="list">
        <div v-for="comment in comments" :key="comment.id" class="comment">
          <div class="comment__meta">
            <span class="comment__author">{{ comment.userEmail }}</span>
            <span class="comment__time">{{ formatTime(comment.createdAt) }}</span>
            <button
              v-if="comment.userId === currentUserId"
              class="comment__delete"
              @click="deleteComment(comment)"
              title="Delete"
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M2 4h12M6 4V2.5h4V4M13 4l-.8 9.5H3.8L3 4"/>
              </svg>
            </button>
          </div>
          <div class="comment__body">{{ comment.content }}</div>
        </div>
      </div>
      <div v-else class="comments__empty">No comments yet</div>

      <div class="comments__form">
        <textarea
          class="comments__textarea"
          v-model="newComment"
          placeholder="Add a comment…"
          rows="2"
          @keydown.ctrl.enter.prevent="submit"
          @keydown.meta.enter.prevent="submit"
        ></textarea>
        <button
          class="comments__submit"
          @click="submit"
          :disabled="!newComment.trim() || submitting"
        >
          <Loading v-if="submitting" size="13" />
          <span v-else>Post</span>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
  import moment from 'moment'
  import Loading from '../common/Loading.vue'

  export default {
    name: 'TaskComments',
    components: { Loading },
    props: ['taskId'],
    data: () => ({
      comments: [],
      newComment: '',
      loading: false,
      submitting: false,
    }),
    computed: {
      currentUserId() {
        return this.$store.getters.user?.id
      }
    },
    watch: {
      taskId: {
        immediate: true,
        handler(id) {
          if (id) this.fetchComments()
          else this.comments = []
        }
      }
    },
    methods: {
      async fetchComments() {
        this.loading = true
        this.comments = await this.$store.dispatch('fetchTaskComments', this.taskId)
        this.loading = false
        await this.$nextTick()
        this.scrollToBottom()
      },
      async submit() {
        const content = this.newComment.trim()
        if (!content || this.submitting) return
        this.submitting = true
        try {
          const comment = await this.$store.dispatch('createTaskComment', {
            taskId: this.taskId,
            content
          })
          this.comments.push(comment)
          this.newComment = ''
          await this.$nextTick()
          this.scrollToBottom()
        } finally {
          this.submitting = false
        }
      },
      async deleteComment(comment) {
        await this.$store.dispatch('deleteTaskComment', {
          taskId: this.taskId,
          commentId: comment.id
        })
        this.comments = this.comments.filter(c => c.id !== comment.id)
      },
      scrollToBottom() {
        const list = this.$refs.list
        if (list) list.scrollTop = list.scrollHeight
      },
      formatTime(date) {
        return moment(date).format('MMM D, HH:mm')
      }
    }
  }
</script>

<style scoped>
.comments {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.comments__header {
  display: flex;
  align-items: center;
  gap: 6px;
}

.comments__icon {
  width: 13px;
  height: 13px;
  color: rgba(0, 0, 0, 0.4);
  flex-shrink: 0;
}

.comments__title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.45);
}

.comments__count {
  font-size: 0.7rem;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.07);
  color: rgba(0, 0, 0, 0.45);
  border-radius: 100px;
  padding: 1px 6px;
}

.comments__loading {
  display: flex;
  justify-content: center;
  padding: 16px 0;
}

.comments__empty {
  font-size: 0.82rem;
  color: rgba(0, 0, 0, 0.35);
  font-style: italic;
  padding: 4px 0;
}

.comments__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 240px;
  overflow-y: auto;
}

.comment {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.025);
  border-radius: var(--app-radius-sm);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.comment__meta {
  display: flex;
  align-items: center;
  gap: 6px;
}

.comment__author {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
}

.comment__time {
  font-size: 0.72rem;
  color: rgba(0, 0, 0, 0.35);
  flex: 1;
}

.comment__delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  color: rgba(0, 0, 0, 0.25);
  transition: color 0.12s, background 0.12s;
  opacity: 0;
}

.comment:hover .comment__delete {
  opacity: 1;
}

.comment__delete:hover {
  color: #c0392b;
  background: rgba(192, 57, 43, 0.08);
}

.comment__delete svg {
  width: 11px;
  height: 11px;
}

.comment__body {
  font-size: 0.82rem;
  color: rgba(0, 0, 0, 0.75);
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.comments__form {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.comments__textarea {
  flex: 1;
  resize: none;
  padding: 8px 10px;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  background: var(--app-bg-input);
  font-size: 0.82rem;
  font-family: inherit;
  color: var(--app-text);
  outline: none;
  transition: border-color var(--app-transition);
  line-height: 1.5;
}

.comments__textarea:focus {
  border-color: rgba(61, 106, 144, 0.5);
}

.comments__textarea::placeholder {
  color: rgba(0, 0, 0, 0.3);
}

.comments__submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  height: 34px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: var(--app-radius-md);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
}

.comments__submit:hover:not(:disabled) {
  opacity: 0.88;
}

.comments__submit:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
